import { ApiClient } from '_core/api/client';
import { ListResponse } from '_core/api/types';
import { serializable } from '_core/serializable';

const BASE_ENDPOINT = '/wagon_etran_notes';

interface IWagonEtranNoteSerialized {
  created: string;
  createdBy: number | null;
  dislocation: number;
  files: string[];
  id: number;
  note: string;
  operation: string;
  wagon: string;
}

interface IWagonEtranNote extends Omit<IWagonEtranNoteSerialized, 'created'> {
  created: Date;
}

@serializable
export class WagonEtranNote implements IWagonEtranNote {
  @serializable.datetime readonly created;
  readonly createdBy;
  readonly dislocation;
  readonly files;
  readonly id;
  readonly note;
  readonly operation;
  readonly wagon;

  static fromJSON(serialized: IWagonEtranNoteSerialized) {
    return serializable.fromJSON(WagonEtranNote, serialized);
  }

  constructor(wagonNote: IWagonEtranNote) {
    this.created = wagonNote.created;
    this.createdBy = wagonNote.createdBy;
    this.dislocation = wagonNote.dislocation;
    this.files = wagonNote.files;
    this.id = wagonNote.id;
    this.note = wagonNote.note;
    this.operation = wagonNote.operation;
    this.wagon = wagonNote.wagon;
  }
}

export async function fetchWagonEtranNotes(
  api: ApiClient,
  {
    page,
    pageSize,
    wagon,
  }: {
    page?: number;
    pageSize?: number;
    wagon?: string;
  } = {}
): Promise<ListResponse<WagonEtranNote>> {
  const response = await api.get<ListResponse<IWagonEtranNoteSerialized>>(
    BASE_ENDPOINT,
    {
      page,
      pageSize,
      wagon,
    }
  );

  return {
    ...response,
    results: response.results.map(WagonEtranNote.fromJSON),
  };
}

export async function createWagonEtranNote(
  api: ApiClient,
  {
    created,
    dislocation,
    files,
    note,
    operation,
    wagon,
  }: {
    created: Date;
    dislocation: number;
    files: string[];
    note: string;
    operation: string;
    wagon: string;
  }
) {
  await api.post(BASE_ENDPOINT, {
    created,
    dislocation,
    files,
    note,
    operation,
    wagon,
  });
}

export async function updateWagonEtranNote(
  api: ApiClient,
  id: number,
  note: WagonEtranNote
) {
  const response = await api.put<IWagonEtranNoteSerialized>(
    `${BASE_ENDPOINT}/${id}`,
    note
  );

  return WagonEtranNote.fromJSON(response);
}

export async function deleteWagonEtranNote(api: ApiClient, id: number) {
  await api.delete(`${BASE_ENDPOINT}/${id}`);
}

export function getDownloadWagonEtranNotesXlsxUrl(
  api: ApiClient,
  wagon: string
) {
  return api.buildApiUrl(`${BASE_ENDPOINT}/download_xlsx`, { wagon });
}
